/**
 * One user: who they are, which clinics they belong to, what staff have done to the account, and
 * the lock (ADM-002). The lock panel only offers what the Worker would allow this staff member, and
 * says why when it offers nothing.
 */
import * as stylex from "@stylexjs/stylex";
import type { AdminUserDetail } from "@lattice/core";
import { ROUTES } from "@lattice/core";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import type { Me } from "./api.ts";
import { api } from "./api.ts";
import { Avatar, Context, CopyValue, Info } from "./bits.tsx";
import { FIELD_INFO } from "./staff.ts";
import { Facts, History, PageHead, Panel, Section, Split, Table } from "./layout.tsx";
import { useLoad } from "./load.ts";
import { Billing } from "./Billing.tsx";
import { ReasonForm } from "./ReasonForm.tsx";
import { href } from "./router.ts";
import { BackToResults, ErrorText, Link, Status } from "./ui.tsx";
import { CLINIC_ROLE, day, moment, refusal, STAFF_ROLE } from "./words.ts";
/** Why this staff member cannot change the lock, or null when they can. */
function lockBlocked(me: Me, detail: AdminUserDetail): string | null {
  if (me.staffRole !== "superuser" && me.staffRole !== "support") return "Only superusers and support can lock or unlock accounts.";
  if (detail.user.staffRole !== null && me.staffRole !== "superuser") return "Only a superuser can lock or unlock a staff account.";
  if (detail.user.id === me.userId) return "You cannot lock your own account.";
  return null;
}
function LockState({
  detail
}: {
  readonly detail: AdminUserDetail;
}) {
  const {
    user
  } = detail;
  if (!user.locked) return <p className="x1ghz6dp x1jty25b">
        Locking ends every session at once. The person cannot sign in until someone unlocks the
        account. They are not emailed.
      </p>;
  const by = detail.history.find(row => row.action === "user_locked")?.actorEmail;
  const when = user.lockedAt === null ? "" : ` ${moment(user.lockedAt)}`;
  return <>
      <p className="x1ghz6dp x1jty25b">
        Locked{when}
        {by === undefined || by === null ? "" : ` by ${by}`}, with this reason:
      </p>
      <p className="x1ghz6dp xuih12o xyumdvf x1t7ytsu x1rzaemg x1mzt3pk">{user.lockReason ?? "None recorded"}</p>
      <p className="x1ghz6dp x1jty25b">
        Unlocking lets them sign in again with a new link. They are not emailed.
      </p>
    </>;
}
function LockPanel({
  me,
  detail,
  onChange
}: {
  readonly me: Me;
  readonly detail: AdminUserDetail;
  readonly onChange: (next: AdminUserDetail) => void;
}) {
  const {
    user
  } = detail;
  const blocked = lockBlocked(me, detail);
  async function change(reason: string): Promise<string | null> {
    const route = user.locked ? ROUTES.adminUnlock : ROUTES.adminLock;
    const result = await api<AdminUserDetail>(route, {
      params: {
        userId: user.id
      },
      body: {
        reason
      }
    });
    if (!result.ok) return refusal(result.error);
    onChange(result.data);
    return null;
  }
  return <Panel padded>
      <div className="x78zum5 xdt5ytf x626il7">
        <h2 className="x1ghz6dp x1rf3zrh x117lpx5 x1e5flnj">
          {user.locked ? "Unlock account" : "Lock account"}
        </h2>
        <LockState detail={detail} />
        {blocked === null ? <ReasonForm key={user.locked ? "unlock" : "lock"} id="lock-reason" hint="Logged with your name. Include the ticket number." action={user.locked ? "Unlock account" : "Lock account"} tone={user.locked ? "primary" : "danger"} onSubmit={change} /> : <p className="x1ghz6dp x1jty25b">{blocked}</p>}
      </div>
    </Panel>;
}
function canVerifyLicence(me: Me): boolean {
  return me.staffRole === "superuser" || me.staffRole === "operations" || me.staffRole === "support";
}
function LicencePanel({
  me,
  detail,
  onChange
}: {
  readonly me: Me;
  readonly detail: AdminUserDetail;
  readonly onChange: (next: AdminUserDetail) => void;
}) {
  const {
    licence
  } = detail;
  if (licence === null) return null;
  async function verify(reason: string): Promise<string | null> {
    const result = await api<AdminUserDetail>(ROUTES.adminVerifyLicence, {
      params: {
        userId: detail.user.id
      },
      body: {
        reason
      }
    });
    if (!result.ok) return refusal(result.error);
    onChange(result.data);
    return null;
  }
  return <Panel padded>
      <div className="x78zum5 xdt5ytf x626il7">
        <h2 className="x1ghz6dp x1rf3zrh x117lpx5 x1e5flnj">
          {licence.status === "verified" ? "Licence verified" : "Verify licence"}
          <Info label="Licence" text={FIELD_INFO.licence} />
        </h2>
        <p className="x1ghz6dp x1jty25b">
          {licence.number} · {licence.state}
          {licence.status === "verified" ? `. Verified${licence.verifiedAt === null ? "" : ` ${moment(licence.verifiedAt)}`}${licence.verifiedByEmail === null ? "" : ` by ${licence.verifiedByEmail}`}.` : ". The clinic cannot submit formulas until this is verified."}
        </p>
        {licence.status === "unverified" && canVerifyLicence(me) ? <ReasonForm id="licence-reason" hint="Logged with your name. Include how you checked the licence." action="Verify licence" onSubmit={verify} /> : null}
      </div>
    </Panel>;
}
function canOnAccount(role: Me["staffRole"]): string {
  if (role === "clinician") return "You can open the record. You cannot lock the account or change billing.";
  if (role === "support") return "You can lock a customer account. You cannot lock staff.";
  if (role === "operations") return "You can verify a licence. You cannot lock an account.";
  return "You can lock, verify and change billing.";
}
function relatedOf(detail: AdminUserDetail) {
  const {
    user
  } = detail;
  const links = detail.memberships.map(membership => ({
    relation: "Clinic",
    label: membership.organizationName,
    to: href.organization(membership.organizationId)
  }));
  if (detail.patientId !== null) links.unshift({
    relation: "Patient",
    label: "Patient record",
    to: href.patient(detail.patientId)
  });
  if (!user.anonymous) links.push({
    relation: "Audit",
    label: "Actions by this account",
    to: href.audit({
      actor: user.email
    })
  });
  return links;
}
function licenceLabel(detail: AdminUserDetail): string {
  const {
    licence
  } = detail;
  if (licence === null) return "None";
  const status = licence.status === "verified" ? "Verified" : "Unverified";
  return `${licence.number} · ${licence.state} · ${status}`;
}
function Detail({
  me,
  detail,
  onChange
}: {
  readonly me: Me;
  readonly detail: AdminUserDetail;
  readonly onChange: (next: AdminUserDetail) => void;
}) {
  const {
    user
  } = detail;
  const displayName = user.anonymous ? "Anonymous visitor" : user.name;
  const related = relatedOf(detail);
  const facts = <Section title="Details">
      <Facts items={[["User id", <CopyValue key="id" value={user.id} label="user id" />], ["Email", user.anonymous ? "None yet. Started from an upload." : user.email], ["Email verified", user.emailVerified ? "Yes" : "No"], ["Created", day(user.createdAt)], ["Active sessions", detail.activeSessions], ["Patient record", detail.patientId === null ? "None" : <Link to={href.patient(detail.patientId)}>Patient record</Link>], ["Licence", licenceLabel(detail)]]} />
    </Section>;
  return <>
      <PageHead back={<BackToResults />} title={<span className="x78zum5 x6s0dn4 x1173whf xeuugli">
            <Avatar name={user.anonymous ? null : user.name} email={user.anonymous ? null : user.email} large />
            {displayName}
          </span>} meta={<>
            {user.locked ? <Status tone="danger">Locked</Status> : <Status tone="ok">Active</Status>}
            <span>{user.staffRole === null ? "Customer" : STAFF_ROLE[user.staffRole]}</span>
            {user.anonymous ? null : <span>{user.email}</span>}
          </>} />
      <Split side={<>
            <Context title="This account" who={displayName} role={user.staffRole === null ? "Customer" : STAFF_ROLE[user.staffRole]} can={canOnAccount(me.staffRole)} links={related} />
            {facts}
            <LicencePanel me={me} detail={detail} onChange={onChange} />
            <LockPanel me={me} detail={detail} onChange={onChange} />
          </>} main={<>
            <Section title="Staff actions" count={detail.history.length}>
              <History rows={detail.history} empty="No staff action on this account yet." />
            </Section>
            {me.staffRole === "clinician" ? <Section title="Billing">
                <p className="x1ghz6dp x1jty25b">
                  Billing is hidden. Clinicians do not change charges.
                </p>
              </Section> : <Billing userId={user.id} />}
            <Section title="Clinics" count={detail.memberships.length}>
              <Table label="Clinics" rows={detail.memberships} rowKey={m => m.organizationId} empty="Not a member of any clinic." columns={[{
          label: "Clinic",
          cell: m => <Link to={href.organization(m.organizationId)}>{m.organizationName}</Link>
        }, {
          label: "Role",
          cell: m => CLINIC_ROLE[m.role]
        }]} />
            </Section>
          </>} />
    </>;
}
export function UserDetail({
  id,
  me
}: {
  readonly id: string;
  readonly me: Me;
}) {
  const [loaded, replace] = useLoad(`user-${id}`, () => api<AdminUserDetail>(ROUTES.adminUser, {
    params: {
      userId: id
    }
  }));
  if (loaded.state === "loading") return <PageHead title="Loading user" />;
  if (loaded.state === "failed") return <>
        <PageHead back={<BackToResults />} title="User" />
        <ErrorText>{refusal(loaded.error)}</ErrorText>
      </>;
  return <Detail me={me} detail={loaded.data} onChange={replace} />;
}